import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, TextField, Box, Chip, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';

const NotificationsTable = ({ 
  notifications = [], 
  paginationModel, 
  onPaginationModelChange, 
  searchTerm, 
  onSearchChange, 
  typeFilter,
  onTypeFilterChange,
  isReadFilter,
  onIsReadFilterChange,
  totalRows, 
  onView,
  onMarkAllAsRead,
  onRefresh,
  loading 
}) => {
  const { t } = useTranslation();

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact':
        return 'primary';
      case 'career':
        return 'success';
      case 'newsletter':
        return 'info';
      case 'order':
        return 'warning';
      case 'billing':
        return 'error';
      case 'b2b':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'contact':
        return t('contact') || 'Contacto';
      case 'career':
        return t('career') || 'Carrera';
      case 'newsletter':
        return t('newsletter') || 'Newsletter';
      case 'order':
        return t('order') || 'Orden';
      case 'billing':
        return t('billing') || 'Facturación';
      case 'b2b':
        return 'B2B';
      default:
        return type || '-';
    }
  };

  const columns = [
    {
      field: 'isRead',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.isRead ? (
            <CheckCircleIcon color="action" fontSize="small" />
          ) : (
            <CheckCircleOutlineIcon color="primary" fontSize="small" />
          )}
        </Box>
      )
    },
    { 
      field: 'title', 
      headerName: t('title') || 'Título', 
      flex: 1.5 
    },
    {
      field: 'description',
      headerName: t('description') || 'Descripción',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}>
          {params.row.description}
        </Box>
      )
    },
    {
      field: 'type',
      headerName: t('type') || 'Tipo',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.type ? (
            <Chip 
              label={getTypeLabel(params.row.type)} 
              size="small" 
              color={getTypeColor(params.row.type)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <IconButton 
            size="small" 
            onClick={() => onView(params.row)}
            color="primary"
            title={t('view_details') || 'Ver detalles'}
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            label={t('search') || 'Buscar'}
            placeholder={t('search_by_title_description') || 'Buscar por título o descripción'}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('type') || 'Tipo'}</InputLabel>
            <Select
              value={typeFilter || ''}
              label={t('type') || 'Tipo'}
              onChange={(e) => onTypeFilterChange(e.target.value || null)}
            >
              <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
              <MenuItem value="contact">{t('contact') || 'Contacto'}</MenuItem>
              <MenuItem value="career">{t('career') || 'Carrera'}</MenuItem>
              <MenuItem value="newsletter">{t('newsletter') || 'Newsletter'}</MenuItem>
              <MenuItem value="order">{t('order') || 'Orden'}</MenuItem>
              <MenuItem value="billing">{t('billing') || 'Facturación'}</MenuItem>
              <MenuItem value="b2b">B2B</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('status') || 'Estado'}</InputLabel>
            <Select
              value={isReadFilter === null ? '' : (isReadFilter ? 'read' : 'unread')}
              label={t('status') || 'Estado'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  onIsReadFilterChange(null);
                } else {
                  onIsReadFilterChange(value === 'read');
                }
              }}
            >
              <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
              <MenuItem value="unread">{t('unread') || 'No leídas'}</MenuItem>
              <MenuItem value="read">{t('read') || 'Leídas'}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <TableRefreshButton loading={loading} onClick={onRefresh} />
          <Button 
            variant="outlined" 
            startIcon={<CheckCircleOutlineIcon />}
            onClick={onMarkAllAsRead}
            disabled={loading}
          >
            {t('mark_all_as_read') || 'Marcar todas como leídas'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 800 }}>
          <DataGrid
            loading={loading}
            rows={notifications}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            rowCount={totalRows}
            getRowId={(row) => row.id}
            getRowClassName={(params) => {
              return params.row.isRead ? '' : 'unread-notification-row';
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'action.selected',
              },
              '& .unread-notification-row': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default NotificationsTable;
